import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';
import { getUrlVariants } from '../../../util/cloudinary';
import {
  GALLERY_IMAGE_DEFAULT_VARIANT,
  GALLERY_IMAGE_OPTIONS,
  GALLERY_IMAGE_VARIANTS,
} from '../listingGalleryImages';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
      ? { aspectWidth: 4, aspectHeight: 3 }
      : hasDimensions
        ? { aspectWidth: w, aspectHeight: h }
        : { aspectWidth: 1, aspectHeight: 1 };
};

const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const {
    intl, rootClassName, className, images, imageVariants,
    thumbnailVariants, cloudinaryImagesIds, cloudinaryUrls = {}, title, category,
    imageSeoTags = {},
  } = props;
  const thumbVariants = thumbnailVariants || imageVariants;

  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  const items = images.map((img, i) => {
    const uuid    = img.id?.uuid;
    const imgMeta = uuid ? (imageSeoTags[uuid] || null) : null;
    const seoTags = imgMeta?.seoTags || null;
    const width   = imgMeta?.width   || null;
    const height  = imgMeta?.height  || null;

    const alt = seoTags?.altText || intl.formatMessage(
      { id: 'ListingImageGallery.imageAltText' },
      { title, index: i + 1, count: images.length, category }
    );
    const thumbAlt = seoTags?.altText || intl.formatMessage(
      { id: 'ListingImageGallery.imageThumbnailAltText' },
      { title, index: i + 1, count: images.length, category }
    );

    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      // renderItem is called with the item alone, so the slide's position has to travel on it —
      // it is what tells the renderer which single image is the LCP candidate.
      index: i,
      alt,
      thumbAlt,
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
      seoTags,
      width,
      height,
      cloudinaryUrl: uuid ? (cloudinaryUrls[uuid] || null) : null,
    };
  });
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };
  const renderItem = item => {
    const seoTags = item.seoTags;
    const width   = item.width;
    const height  = item.height;

    // Only slide 0 is the LCP candidate. This used to be hardcoded true for every slide, which
    // gave each of a listing's photos loading="eager" + fetchpriority="high" + decoding="sync"
    // (see ResponsiveImage): a ten-photo listing fired ten full-size downloads that competed
    // with the actual LCP image for bandwidth and then decoded them synchronously on the main
    // thread. The remaining slides fall back to ResponsiveImage's lazy/async/auto defaults;
    // they stay in the DOM (so every image is still in the SSR HTML for crawlers) but sit
    // outside the viewport behind the carousel's translate offset, so the browser defers them
    // until the user actually slides.
    const isHeroSlide = item.index === 0;

    const renderCloudinaryImageMaybe = item.cloudinaryUrl
      ? {
          renderCloudinaryImage: {
            secure_url: item.cloudinaryUrl,
            alt: item.alt,
            // Shared with the <link rel="preload"> for slide 0 — see listingGalleryImages.js.
            variantsToRender: GALLERY_IMAGE_VARIANTS,
            defaultVariant: GALLERY_IMAGE_DEFAULT_VARIANT,
            cloudinaryOptions: GALLERY_IMAGE_OPTIONS,
            cloudinaryClassName: css.item,
            // No style override — ResponsiveImage defaultStyles apply (height/width: 100%)
            // so the img fills the AspectRatioWrapper, matching the Sharetribe path.
            // object-fit: contain from .itemCentering img handles any AR difference.
          },
        }
      : {};

    // Inline card (Figma node 16754:146858 "Featured Image") is a fixed square slot,
    // cropped via object-fit: cover, regardless of the source photo's own aspect ratio.
    // The fullscreen lightbox is a different context (not covered by this mock) and keeps
    // showing the photo uncropped at its real aspect ratio.
    const wrapperWidth = isFullscreen ? (aspectWidth || 1) : 1;
    const wrapperHeight = isFullscreen ? (aspectHeight || 1) : 1;

    return (
      <figure className={css.galleryFigure} style={{ margin: 0 }}>
        <AspectRatioWrapper
          width={wrapperWidth}
          height={wrapperHeight}
          className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
        >
          <div className={classNames(css.itemCentering, !isFullscreen && css.itemCenteringSquare)}>
            <ResponsiveImage
              rootClassName={css.item}
              image={item.image}
              alt={item.alt}
              title={seoTags?.titleTag || ''}
              variants={imageVariants}
              {...(width  ? { width }  : {})}
              {...(height ? { height } : {})}
              {...imageSizesMaybe}
              isCritical={isHeroSlide}
              {...renderCloudinaryImageMaybe}
            />
          </div>
        </AspectRatioWrapper>
        {/* {seoTags?.caption && !isFullscreen && (
          <figcaption className={css.galleryCaption}>
            {seoTags.caption}
          </figcaption>
        )} */}
      </figure>
    );
  };
  const renderThumbInner = item => {
    const cloudinaryThumbUrl = item.cloudinaryUrl
      ? getUrlVariants(item.cloudinaryUrl, 'ras_thumb', { quality: 'auto:low', format: 'auto', dpr: 'auto' }).imageUrl
      : null;
    const thumbnail = item.thumbnail;
    const src = cloudinaryThumbUrl || thumbnail?.url || thumbnail;

    return (
      <div aria-hidden="true" role="presentation">
        {src
          ? (
            <img
              src={src}
              alt={item.thumbAlt || ''}
              width={thumbnail?.width  || 100}
              height={thumbnail?.height || 75}
              loading="lazy"
            />
          )
          : null
        }
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };
  const onSlide = (index) => {
    setCurrentIndex(index);
};


  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreen) => {
    return isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <>
      <div className={css.imageCountContainer}>
          {`${currentIndex + 1}/${images.length}`}
      </div>
      <button className={css.openFullscreen} onClick={onClick}>
          <svg width="36" height="36" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z" fill="white" />
              <path d="M19.7239 11.3333H17.3333V10H22V14.6667H20.6667V12.2761L17.8047 15.1381L16.8619 14.1953L19.7239 11.3333ZM10 17.3333H11.3333V19.7239L14.1953 16.8619L15.1381 17.8047L12.2761 20.6667H14.6667V22H10V17.3333Z" fill="#101828" />
          </svg>
      </button>
  </>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);
  return (
    <ReactImageGallery
      additionalClass={classes}
      items={items}
      renderItem={renderItem}
      renderThumbInner={renderThumbInner}
      onScreenChange={onScreenChange}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      renderFullscreenButton={renderFullscreenButton}
      onSlide={onSlide}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

ListingImageGallery.defaultProps = {
  rootClassName: null,
  className: null,
  thumbnailVariants: null,
};

const { string, arrayOf } = PropTypes;

ListingImageGallery.propTypes = {
  rootClassName: string,
  className: string,
  images: arrayOf(propTypes.image).isRequired,
  imageVariants: arrayOf(string).isRequired,
  thumbnailVariants: arrayOf(string),
  imageSeoTags: PropTypes.object,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(ListingImageGallery);
